/**
 * 批量上架 Session 辅助 Hook
 * 封装 Vuex 操作，简化组件中的使用
 */
import { useStore } from '@/utils/useUtils'
import { computed } from 'vue'
import { getEnum } from '@/api/global'

// 导出枚举供组件使用
export { MODE_ENUM, STEP_ENUM } from '@/store/modules/batchPublish'

// 全局的 fetchEnums Promise 缓存，用于防止并发请求
let fetchEnumsPromise = null

export function useBatchSession() {
  const store = useStore()

  // State - 使用 computed 保持响应性
  const state = computed(() => store.state.batchPublish)
  const sessionId = computed(() => store.state.batchPublish.sessionId)
  const sessionLoading = computed(() => store.state.batchPublish.sessionLoading)
  const sessionExpired = computed(() => store.state.batchPublish.sessionExpired)
  const mode = computed(() => store.state.batchPublish.mode)
  const planId = computed(() => store.state.batchPublish.planId)
  const currentStep = computed(() => store.state.batchPublish.currentStep)
  const globalCarInfo = computed(() => store.state.batchPublish.globalCarInfo)
  const carModelList = computed(() => store.state.batchPublish.carModelList)
  const selectedModelIds = computed(
    () => store.state.batchPublish.selectedModelIds,
  )
  const planDetail = computed(() => store.state.batchPublish.planDetail)
  const enterpriseId = computed(() => store.state.batchPublish.enterpriseId)
  const enterpriseInfo = computed(() => store.state.batchPublish.enterpriseInfo)
  const enterpriseList = computed(() => store.state.batchPublish.enterpriseList)
  const cooperationPlatform = computed(
    () => store.state.batchPublish.cooperationPlatform,
  )
  const storeList = computed(() => store.state.batchPublish.storeList)
  const storeSelectAll = computed(() => store.state.batchPublish.storeSelectAll)
  const storeSelectedIds = computed(
    () => store.state.batchPublish.storeSelectedIds,
  )
  const previewData = computed(() => store.state.batchPublish.previewData)
  const previewList = computed(() => store.state.batchPublish.previewList)
  const enums = computed(() => store.state.batchPublish.enums)
  const carAgeOptions = computed(() => store.state.batchPublish.enums.carAge)
  const mileageOptions = computed(
    () => store.state.batchPublish.enums.carMileage,
  )

  // Getters
  const hasSession = computed(() => store.getters['batchPublish/hasSession'])
  const isBatchMode = computed(() => store.getters['batchPublish/isBatchMode'])
  const isSingleMode = computed(
    () => store.getters['batchPublish/isSingleMode'],
  )
  const canProceed = computed(() => store.getters['batchPublish/canProceed'])
  const selectedStoreCount = computed(
    () => store.getters['batchPublish/selectedStoreCount'],
  )
  const elapsedTime = computed(() => store.getters['batchPublish/elapsedTime'])

  // Mutations 封装
  const initState = (options) => {
    store.commit('batchPublish/INIT_STATE', options)
  }

  const resetState = () => {
    store.commit('batchPublish/RESET_STATE')
  }

  const setSessionId = (id) => {
    store.commit('batchPublish/SET_SESSION_ID', id)
  }

  const setSessionLoading = (loading) => {
    store.commit('batchPublish/SET_SESSION_LOADING', loading)
  }

  const setSessionExpired = (expired) => {
    store.commit('batchPublish/SET_SESSION_EXPIRED', expired)
  }

  const setCurrentStep = (step) => {
    store.commit('batchPublish/SET_CURRENT_STEP', step)
  }

  const nextStep = () => {
    store.commit('batchPublish/NEXT_STEP')
  }

  const prevStep = () => {
    store.commit('batchPublish/PREV_STEP')
  }

  const setGlobalCarInfo = (info) => {
    store.commit('batchPublish/SET_GLOBAL_CAR_INFO', info)
  }

  const setCarAge = (carAge) => {
    store.commit('batchPublish/SET_CAR_AGE', carAge)
  }

  const setMileage = (mileage) => {
    store.commit('batchPublish/SET_MILEAGE', mileage)
  }

  const setHasLicense = (hasLicense) => {
    store.commit('batchPublish/SET_HAS_LICENSE', hasLicense)
  }

  const setCarModelList = (list) => {
    store.commit('batchPublish/SET_CAR_MODEL_LIST', list)
  }

  const setSelectedModelIds = (ids) => {
    store.commit('batchPublish/SET_SELECTED_MODEL_IDS', ids)
  }

  const toggleModelSelection = (modelId, selected) => {
    store.commit('batchPublish/TOGGLE_MODEL_SELECTION', { modelId, selected })
  }

  const setPlanDetail = (detail) => {
    store.commit('batchPublish/SET_PLAN_DETAIL', detail)
  }

  const setEnterpriseList = (list) => {
    store.commit('batchPublish/SET_ENTERPRISE_LIST', list)
  }

  const setEnterprise = (id, info = null) => {
    store.commit('batchPublish/SET_ENTERPRISE', { id, info })
  }

  const setCooperationPlatform = (platform) => {
    store.commit('batchPublish/SET_COOPERATION_PLATFORM', platform)
  }

  const setStoreList = (list) => {
    store.commit('batchPublish/SET_STORE_LIST', list)
  }

  const setStoreSelectAll = (selectAll) => {
    store.commit('batchPublish/SET_STORE_SELECT_ALL', selectAll)
  }

  const setStoreExcludeIds = (ids) => {
    store.commit('batchPublish/SET_STORE_EXCLUDE_IDS', ids)
  }

  const setStoreSelectedIds = (ids) => {
    store.commit('batchPublish/SET_STORE_SELECTED_IDS', ids)
  }

  const toggleStoreSelection = (storeId, selected) => {
    store.commit('batchPublish/TOGGLE_STORE_SELECTION', { storeId, selected })
  }

  const setPreviewData = (data) => {
    store.commit('batchPublish/SET_PREVIEW_DATA', data)
  }

  const setPreviewList = (list) => {
    store.commit('batchPublish/SET_PREVIEW_LIST', list)
  }

  // Actions 封装
  const init = (options) => {
    return store.dispatch('batchPublish/init', options)
  }

  const reset = () => {
    return store.dispatch('batchPublish/reset')
  }

  const fetchEnums = async () => {
    // 如果已加载，直接返回
    if (enums.value.loaded) {
      return Promise.resolve()
    }

    // 如果正在加载中，返回正在进行的 Promise（防止并发请求）
    if (fetchEnumsPromise) {
      return fetchEnumsPromise
    }

    // 创建加载 Promise
    fetchEnumsPromise = (async () => {
      try {
        const [carAgeRes, mileageRes] = await Promise.all([
          getEnum({ type: 'carAgeEnum' }),
          getEnum({ type: 'carMileageEnum' }),
        ])

        store.commit('batchPublish/SET_ENUMS', {
          carAge: carAgeRes?.code === 1 ? carAgeRes.data || [] : [],
          carMileage: mileageRes?.code === 1 ? mileageRes.data || [] : [],
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load enums:', error)
        // 即使失败也标记为已加载，避免无限重试
        store.commit('batchPublish/SET_ENUMS', { carAge: [], carMileage: [] })
      } finally {
        // 请求完成后清除 Promise 缓存
        fetchEnumsPromise = null
      }
    })()

    return fetchEnumsPromise
  }

  return {
    // State
    state,
    sessionId,
    sessionLoading,
    sessionExpired,
    mode,
    planId,
    currentStep,
    globalCarInfo,
    carModelList,
    selectedModelIds,
    planDetail,
    enterpriseId,
    enterpriseInfo,
    enterpriseList,
    cooperationPlatform,
    storeList,
    storeSelectAll,
    storeSelectedIds,
    previewData,
    previewList,
    enums,
    carAgeOptions,
    mileageOptions,

    // Getters
    hasSession,
    isBatchMode,
    isSingleMode,
    canProceed,
    selectedStoreCount,
    elapsedTime,

    // Mutations
    initState,
    resetState,
    setSessionId,
    setSessionLoading,
    setSessionExpired,
    setCurrentStep,
    nextStep,
    prevStep,
    setGlobalCarInfo,
    setCarAge,
    setMileage,
    setHasLicense,
    setCarModelList,
    setSelectedModelIds,
    toggleModelSelection,
    setPlanDetail,
    setEnterpriseList,
    setEnterprise,
    setCooperationPlatform,
    setStoreList,
    setStoreSelectAll,
    setStoreExcludeIds,
    setStoreSelectedIds,
    toggleStoreSelection,
    setPreviewData,
    setPreviewList,

    // Actions
    init,
    reset,
    fetchEnums,
  }
}
